import { useTimezoneSelect, allTimezones } from 'react-timezone-select'
import { getAllCountries, getTimezonesForCountry } from 'countries-and-timezones';
import Select from './Select'
import PropTypes from 'prop-types';
import { FloatingLabel } from './FloatingLabel';
import { useEffect, useMemo, useState } from 'react';
import { ReactNodeDialog } from '../utils/dialogUtlities';
import { Row } from './Grid';
import { Col } from 'react-bootstrap';
import TypeaheadInput from './TypeaheadInput';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import { dynamicSort } from '../utils/tableUtilities';
import Icon from './Icon';
import { withComponentCapabilities } from './capabilities/componentCapabilities';

const labelStyle = 'original'
const timezones = {
  ...allTimezones
}

export const customCtrlDef = {
  def: (t, tools) => ({
      form: [{
          borderless: true,
          rows: [
              ...tools.labelFields(t),
              ...tools.fieldFields(t),
              ...tools.commonFields(t)
          ]
      }]
  }),
  control: ({ def, href, ...props }) => <TimezoneInput href={href ?? def.href} {...props}></TimezoneInput>
};

/**
 * A dropdown to select timezones around the world
 * @component
 */
const TimezoneInput = withComponentCapabilities(({ value, placeholder, wildcard, hideLabel = false, required = false, disabled = false, readonly = false, defaultReadonlyValue = '', onChange, href, ...props }) => {
  const { t } = useTranslation();
  const [tz, setTZ] = useState(value);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState();
  const { options, parseTimezone } = useTimezoneSelect({ labelStyle, timezones })
  const countries = useMemo(() => {
    let result = Object.values(getAllCountries()).map(country => ({ label: country.name, value: country.id }));
    result.sort();
    return result;
  }, []);

  useEffect(() => {
    setTZ(value);
  }, [value])
  
  if (readonly) {
    return <FloatingLabel hideLabel={hideLabel} placeholder={placeholder} required={required} value={true}>{tz ? parseTimezone(tz).label : defaultReadonlyValue}</FloatingLabel>
  }

  const closeDialog = () => {
    setShowDialog(false);
    setSelectedCountry();
  }

  let selectedTimezones = null;
  if (selectedCountry) {
    selectedTimezones = getTimezonesForCountry(selectedCountry).map(c => {
        try {
            return parseTimezone(c.name)
        } catch {
            return null;
        }
    }).filter(tz => tz).map(tz => ({ label: tz.label, id: tz.value, offset: tz.offset }));
    selectedTimezones = selectedTimezones.filter((tz, idx) => selectedTimezones.findIndex(t => t.label === tz.label) === idx);
    selectedTimezones.sort(dynamicSort(['-offset', 'label']));
  }

  const setTimezone = v => {
        const newTZ = parseTimezone(v); 
        onChange(newTZ.value); 
  }

  return (<>
    <ReactNodeDialog showModal={showDialog} title={t('timezoneSearch')} onClose={closeDialog} style={{minHeight: '400px'}}>
      <Row>
        <Col>
          <TypeaheadInput hideLabel={hideLabel} options={countries} placeholder={t('country')} value={selectedCountry} lockdown={true} optLabel={v => v.label} optValue={v => v.value} optValueComparator={(opt, idx, v) => opt?.value === v} onChange={v => {
            setSelectedCountry(v?.id)
          }}></TypeaheadInput>
        </Col>
      </Row>
      <Row>
        <Col>
          <ul>
            { selectedTimezones ? selectedTimezones.map(tz => <li><Button placeholder="" onClick={() => { setTimezone(tz.id); closeDialog(); }} variant="link">{tz.label}</Button></li>) : null}
          </ul>
        </Col>
      </Row>
    </ReactNodeDialog>
    <Row>
      <Col>
        <Select value={tz} placeholder={placeholder} wildcard={wildcard} required={required} onChange={v => setTimezone(v)} disabled={disabled} {...props} options={options} labelField="label" valueField="value" href={href}></Select>
      </Col>
      <Col xs="auto">
        <FloatingLabel placeholder='&nbsp;' value={tz}>
          <Button variant="default" onClick={() => setShowDialog(true)}><Icon variant="solid" kind="magnifying-glass-location"></Icon></Button>
        </FloatingLabel>
      </Col>
    </Row>
    </>
  )
}, {

});

TimezoneInput.propTypes = {
    /**
     * The timezone as an IANA timezone name (eg. Australia/Brisbane)
     */
    value: PropTypes.string,

    /**
     * Function to call when the value has been changed
     */
    onChange: PropTypes.func,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * When provided indicates the dropdown should allow a null selection which is to be treated as a wildcard.
     * The string supplied is used as the label of the wildcard entry (standard use would be something like
     * '(any)' or '(all)' depending on the context)
     */
    wildcard: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Render the control value as text rather an input control
     */
    readonly: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * The string to display in readonly mode if value is null or undefined
     */
    defaultReadonlyValue: PropTypes.string,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default TimezoneInput;